import { useEffect, useState } from 'react';
import { useActionData, useSubmit, useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import Page from 'client/components/aria/Page/Page';
import WorkpackForm from './WorkpackForm/WorkpackForm';
import { jsonToFormData } from 'client/utils/helpers/Workpack/Workpack';
import { MAP_PAGE, MAP_PAGE_WORKPACK, HOME_PAGE, WORKPACKS_PAGE } from 'client/utils/routes/routeConstants';
import ErrorSummary from 'client/components/GDS/ErrorSummary/ErrorSummary';
import BackLink from 'client/components/aria/BackLink/BackLink';
import { permissions } from 'client/utils/constants/permissions';
import { useHasUserPermission } from 'client/utils/auth/userPermissions';
import PurposeSelectionForm from './WorkpackTypeSelectionForm/WorkpackTypeSelectionForm';
import { Breadcrumbs } from 'client/components/GDS/Breadcrumbs/Breadcrumbs';
import WorkpackPageTitle from 'client/pages/WorkpacksPage/components/WorkpackPageTitle/WorkpackPageTitle';
import { enquiryFeatureFlag } from 'client/utils/constants/frontEndConfig';
import WorkpackAlertWarning from '../components/WorkpackAlerts/WorkpackAlertWarning/WorkpackAlertWarning';
import { setDraftWorkpack } from 'client/store/workpackSlice/workpackSlice';

const CreateWorkpackPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { enquiry, error, createdWorkpackReqId } = useActionData() || {};
    const submit = useSubmit();
    const [showSummary, setShowSummary] = useState(false);
    const [errors, setErrors] = useState([]);
    const [editingPurpose, setEditingPurpose] = useState(null); // Store purpose while editing
    const hasEmergencyPermission = useHasUserPermission(permissions.EMERGENCY_I2D_CREATE);
    const hasI2dPermission = useHasUserPermission(permissions.I2D_CREATE);
    const hasEmergencyAndI2dCreatePermission = hasEmergencyPermission && hasI2dPermission;

    useEffect(() => {
        if (createdWorkpackReqId) {
            navigate(WORKPACKS_PAGE, { replace: true });
        } else if (!draftEnquiry) {
            navigate(MAP_PAGE);
        }
    }, []);

    const [draftEnquiry, setDraftEnquiry] = useState(!enquiry
        ? null
        : {
            ...enquiry,
            isEmergency: enquiry.isEmergency ?? (() => {
                // if isEmergency is not set in the action data (e.g. from a previous submit)
                // then determine based on user's permissions
                if (hasEmergencyAndI2dCreatePermission) {
                    // could be either - set to null to ensure radio buttons aren't selected by default
                    return null;
                } else if (hasI2dPermission) {
                    return false;
                } else if (hasEmergencyPermission) {
                    return true;
                }
                return null;
            })(),
            // Auto-set purpose to 'prework' when enquiry flow is disabled
            purpose: enquiry.purpose || (!enquiryFeatureFlag ? 'prework' : null)
        });

    const handleSetPurpose = (purpose) => {
        // For prework/other purposes, determine isEmergency based on user permissions
        // Planned and emergency purposes will show "Make Enquiry" button instead
        let isEmergency = false;
        if (hasEmergencyPermission && !hasI2dPermission) {
            // Only has emergency permission
            isEmergency = true;
        }

        setDraftEnquiry(prev => ({
            ...prev,
            purpose,
            isEmergency
        }));
        setEditingPurpose(null); // Clear editing purpose after setting
    };

    const clearDraftPurpose = () => {
        // Store the current purpose before clearing so it can be edited
        setEditingPurpose(draftEnquiry?.purpose);
        setShowSummary(false);
        setDraftEnquiry(prev => ({ ...prev, purpose: null, isEmergency: null }));
    };

    const handleEdit = () => {
        dispatch(setDraftWorkpack(draftEnquiry));
        const formData = jsonToFormData(draftEnquiry);
        submit(formData, {
            action: MAP_PAGE_WORKPACK,
            method: 'POST'
        });
    };

    const breadcrumbsData = [
        { name: intl.formatMessage({ id: 'navbar.link.home' }), link: HOME_PAGE },
        { name: intl.formatMessage({ id: 'navbar.link.map' }), link: MAP_PAGE },
        ...(draftEnquiry?.purpose === null
            ? [{ name: intl.formatMessage({ id: 'createWorkpackPage.purpose.breadcrumb' }), ariaCurrent: 'page' }]
            : [
                { name: intl.formatMessage({ id: 'createWorkpackPage.purpose.breadcrumb' }), onClick: clearDraftPurpose },
                { name: intl.formatMessage({ id: 'createWorkpackPage.title' }), ariaCurrent: 'page' }
            ]
        )
    ];

    const handleBack = () => {
        if (showSummary) {
            // Back from summary page
            setShowSummary(false);
        } else if (draftEnquiry?.purpose !== null) {
            if (!enquiryFeatureFlag) {
                navigate(MAP_PAGE);
            } else {
                // Back from form to purpose selection
                clearDraftPurpose();
            }
        } else {
            // Back to map
            navigate(MAP_PAGE);
        }

        const navigationElement = document.getElementById('navigation');
        if (navigationElement) {
            navigationElement.scrollIntoView();
        }
    };

    // Show back button when on summary page or when purpose is selected (on the form)
    const showBackButton = showSummary || draftEnquiry?.purpose !== null;

    return draftEnquiry && (
        <Page
            title={intl.formatMessage({ id: 'createWorkpackPage.page.title' })}
            data-testid="createWorkpackPage">
            <div className="govuk-grid-row govuk-!-margin-top-4">
                <div className="govuk-grid-column-full">
                    {errors.length > 0 && <ErrorSummary errors={errors} />}
                    <WorkpackPageTitle error={error} isEmergency={draftEnquiry?.isEmergency} showWarning={false} id="createWorkpackPageTitle" >
                        {!error && showBackButton && (
                            <div className="govuk-grid-column-full">
                                <BackLink onClick={handleBack} data-testid="create-workpack-back-link">
                                    {intl.formatMessage({ id: 'general.back' })}
                                </BackLink>
                            </div>
                        )}
                        {!error && draftEnquiry?.purpose === null && (
                            <div data-testid="create-workpack-breadcrumbs">
                                <Breadcrumbs items={breadcrumbsData} />
                            </div>
                        )}
                        {!error && (showSummary
                            ? intl.formatMessage({ id: 'createWorkpackPage.title.summary' })
                            : (draftEnquiry?.purpose === null
                                ? intl.formatMessage({ id: 'createWorkpackPage.purpose.title' })
                                : (draftEnquiry?.isEmergency === null
                                    ? intl.formatMessage({ id: 'createWorkpackPage.select.enquiry.type.title' })
                                    : intl.formatMessage({ id: 'createWorkpackPage.title' }))))
                        }
                        {!error && (
                            <WorkpackAlertWarning />
                        )}
                        {!error && (
                            <p className="govuk-body-m govuk-!-margin-top-6" data-testid="create-workpack-asset-owner-notification">
                                {intl.formatMessage({ id: 'createWorkpackPage.assetOwnerNotification' })}
                            </p>
                        )}
                    </WorkpackPageTitle>
                </div>
            </div>
            <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
            {/* STEP 1: WORK PACK PURPOSE SELECTION - Select purpose (prework, planned, emergency, other) */}
            {!error && draftEnquiry?.purpose === null
                ? <div data-testid="step-1-purpose-selection">
                    <PurposeSelectionForm
                        setPurpose={handleSetPurpose}
                        setShowSummary={setShowSummary}
                        enquiry={editingPurpose ? { ...draftEnquiry, purpose: editingPurpose } : draftEnquiry}
                        canSelectEmergency={hasEmergencyPermission}
                    />
                </div>
                /* STEP 2 & 3: WORK PACK FORM - Contains both details form and summary */
                : !error && <div className="govuk-grid-row" data-testid={showSummary ? 'step-3-summary' : 'step-2-details-form'}>
                    <div className="govuk-grid-column-full">
                        <WorkpackForm
                            enquiry={draftEnquiry}
                            onChange={setDraftEnquiry}
                            showSummary={showSummary}
                            setShowSummary={setShowSummary}
                            handleEdit={handleEdit}
                            handleChangePurpose={clearDraftPurpose}
                            setErrors={setErrors}
                        />
                    </div>
                </div>
            }
        </Page>
    );
};

export default CreateWorkpackPage;
